/*
  Steigen cart rewards
  - Lets the customer pick their free gift once the cart reaches the gift amount: one tap on a colour adds it
    (caps are one size; if a gift ever has sizes the picker grows a size step on its own)
    (amounts, wording and gift products are set in the theme editor: Theme settings > Cart rewards)
  - Keeps the reward honest: removes the free gift if the cart drops below the gift amount,
    and never lets more than one free gift sit in the cart
  Works with Horizon's cart drawer: every change is announced with a cart update event,
  so the drawer, the cart count and the gold progress bar all refresh together.
*/
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const PROP = '_stg_reward';
function giftMin() {
  return (window.stgRewards && window.stgRewards.giftMin) || 18000;
}
let busy = false;

/*
  The customer's cap choice is remembered on this device. If the cap drops out of the cart for a reason
  the customer didn't choose (the cart dipped below the amount, or checkout moved them to another country
  for a moment), it comes straight back in the same colour once they qualify again.
  Taking the cap out themselves (bin or minus) forgets the choice.
*/
const CHOICE_KEY = 'stg-cap-choice';
const CHOICE_DAYS = 14;
let lastRewardVariant = null;
let autoTried = false;

function giftIds() {
  return ((window.stgRewards && window.stgRewards.giftIds) || []).map(Number);
}
function getChoice() {
  let choice = null;
  try { choice = JSON.parse(localStorage.getItem(CHOICE_KEY) || 'null'); } catch (e) { choice = null; }
  if (!choice || !choice.id) return null;
  if (Date.now() - (choice.at || 0) > CHOICE_DAYS * 86400000) return null;
  if (giftIds().length && giftIds().indexOf(Number(choice.id)) === -1) return null;
  return choice;
}
function setChoice(id) {
  try { localStorage.setItem(CHOICE_KEY, JSON.stringify({ id: Number(id), at: Date.now() })); } catch (e) { /* private mode */ }
}
function clearChoice() {
  try { localStorage.removeItem(CHOICE_KEY); } catch (e) { /* private mode */ }
}

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
    if (ids.indexOf(el.dataset.sectionId) === -1) ids.push(el.dataset.sectionId);
  });
  return ids;
}

function request(url, body) {
  const options = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  if (body) {
    options.method = 'POST';
    options.body = JSON.stringify(body);
  }
  return fetch(url, options).then(function (response) {
    return response.json();
  });
}

function broadcast(cart, sections) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'stg-rewards', {
      source: 'stg-rewards',
      itemCount: cart.item_count,
      sections: sections,
    })
  );
}

async function claim(button) {
  if (busy) return;
  const root = button.closest('[data-stg-claim-root]');
  const swapLine = root && root.dataset.stgSwapLine ? Number(root.dataset.stgSwapLine) : 0;
  /* Tapping the colour that is already in the cart: nothing to swap, just tuck the picker away */
  if (swapLine && button.getAttribute('aria-pressed') === 'true') {
    root.hidden = true;
    const bar = root.closest('[data-stg-rewards]');
    const change = bar && bar.querySelector('[data-stg-change]');
    if (change) change.setAttribute('aria-expanded', 'false');
    return;
  }
  busy = true;
  button.setAttribute('aria-busy', 'true');
  try {
    /* Changing colour or size: take the current free cap out first, then add the new one */
    if (swapLine) {
      await request('/cart/change.js', { line: swapLine, quantity: 0 });
    }
    const properties = {};
    properties[PROP] = 'Free gift';
    const added = await request('/cart/add.js', {
      items: [{ id: Number(button.dataset.stgClaim), quantity: 1, properties: properties }],
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });
    if (added.status && added.status !== 200) throw new Error(added.description || added.message);
    setChoice(button.dataset.stgClaim);
    lastRewardVariant = Number(button.dataset.stgClaim);
    const cart = await request('/cart.js');
    document.dispatchEvent(new CustomEvent('stg:gift-claimed', { detail: { cart: cart, swapped: !!swapLine } }));
    broadcast(cart, added.sections);
  } catch (error) {
    console.warn('[steigen] could not add the free cap', error);
  } finally {
    busy = false;
    button.removeAttribute('aria-busy');
  }
}

function giftOn() {
  return !(window.stgRewards && window.stgRewards.giftOn === false);
}

function rewardLines(cart) {
  const lines = [];
  cart.items.forEach(function (item, index) {
    if (item.properties && item.properties[PROP]) lines.push({ item: item, line: index + 1 });
  });
  return lines;
}

/*
  The cap is only taken out when the customer is genuinely no longer entitled to it:
  the cart drops below the amount, the offer is switched off, or Shopify still charges for it
  after a fresh recalculation. A cap that briefly shows a price (for example after a trip to
  checkout with an address in another country) is kept, and the cart is recalculated instead.
*/
async function autoAdd(cart) {
  if (autoTried || busy || !giftOn()) return;
  const choice = getChoice();
  if (!choice) return;
  if (!cart.item_count || cart.items_subtotal_price < giftMin()) return;
  let skipped = false;
  try { skipped = sessionStorage.getItem('stg-gift-skipped') === '1'; } catch (e) { skipped = false; }
  if (skipped) return;
  autoTried = true;
  busy = true;
  let added = null;
  try {
    const properties = {};
    properties[PROP] = 'Free gift';
    added = await request('/cart/add.js', {
      items: [{ id: Number(choice.id), quantity: 1, properties: properties }],
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });
    if (added.status && added.status !== 200) throw new Error(added.description || added.message);
    lastRewardVariant = Number(choice.id);
    const fresh = await request('/cart.js');
    broadcast(fresh, added.sections);
  } catch (error) {
    console.warn('[steigen] could not bring the free cap back', error);
    added = null;
  } finally {
    busy = false;
  }
  if (added) return enforce(0, false);
}

/* The free cap used to be the normal cap made free by a discount. It is now its own $0 gift item (same handle
   plus -free-gift). A cart still holding an old-style free cap swaps it for the new one in the same colour. */
function giftForBaseHandle(handle) {
  const map = (window.stgRewards && window.stgRewards.giftHandles) || {};
  const want = String(handle || '') + '-free-gift';
  for (const id in map) {
    if (map[id] === want) return Number(id);
  }
  return 0;
}
async function swapStale(cart) {
  const ids = giftIds();
  if (!ids.length) return false;
  const stale = rewardLines(cart).filter(function (entry) {
    return ids.indexOf(Number(entry.item.variant_id)) === -1;
  });
  if (!stale.length) return false;
  const swap = giftForBaseHandle(stale[0].item.handle);
  if (swap) setChoice(swap);
  let swapped = false;
  busy = true;
  try {
    const updated = await request('/cart/change.js', {
      line: stale[0].line,
      quantity: 0,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });
    if (updated && updated.items) {
      lastRewardVariant = null;
      autoTried = false;
      swapped = true;
      broadcast(updated, updated.sections);
    }
  } catch (error) {
    console.warn('[steigen] could not swap the old free cap', error);
  } finally {
    busy = false;
  }
  return swapped;
}

async function enforce(attempt, fromUser) {
  if (busy) return;
  attempt = attempt || 0;
  const cart = await request('/cart.js');
  if (await swapStale(cart)) return enforce(0, false);
  const lines = rewardLines(cart);
  if (!lines.length) {
    /* The cap was there a moment ago and the customer took it out themselves: forget their choice */
    if (fromUser && lastRewardVariant) clearChoice();
    lastRewardVariant = null;
    return autoAdd(cart);
  }
  lastRewardVariant = lines[0].item.variant_id;
  if (!getChoice()) setChoice(lines[0].item.variant_id);

  const first = lines[0];
  const rewardValue = lines.reduce(function (sum, entry) {
    return sum + entry.item.final_line_price;
  }, 0);
  const qualifying = cart.items_subtotal_price - rewardValue;

  let change = null;
  if (!giftOn()) change = { line: first.line, quantity: 0 };
  else if (qualifying < giftMin()) change = { line: first.line, quantity: 0 };
  else if (lines.length > 1) change = { line: lines[lines.length - 1].line, quantity: 0 };
  else if (first.item.quantity > 1) change = { line: first.line, quantity: 1 };
  else if (first.item.final_line_price > 0 && attempt === 0) {
    /* Re-save the line so Shopify re-applies the free cap for this country, then look again */
    busy = true;
    try {
      const refreshed = await request('/cart/change.js', {
        line: first.line,
        quantity: 1,
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      });
      broadcast(refreshed, refreshed.sections);
    } catch (error) {
      console.warn('[steigen] cart refresh failed', error);
    } finally {
      busy = false;
    }
    return enforce(1, false);
  } else if (first.item.final_line_price > 0) {
    /* Shopify still charges for it here, so it isn't free in this country or with this code: don't keep re-adding it */
    change = { line: first.line, quantity: 0 };
    autoTried = true;
  }
  if (!change) return;

  busy = true;
  try {
    change.sections = sectionIds().join(',');
    change.sections_url = window.location.pathname;
    const updated = await request('/cart/change.js', change);
    if (change.quantity === 0) {
      /* Our removal, not the customer's: keep their colour so it can come back when they qualify again */
      lastRewardVariant = null;
      if (change.line === first.line && !(first.item.final_line_price > 0 && attempt > 0)) autoTried = false;
    }
    broadcast(updated, updated.sections);
  } catch (error) {
    console.warn('[steigen] reward check failed', error);
  } finally {
    busy = false;
  }
}

document.addEventListener('click', function (event) {
  const colour = event.target.closest ? event.target.closest('[data-stg-colour]') : null;
  if (!colour) return;
  event.preventDefault();
  const root = colour.closest('[data-stg-claim-root]');
  if (!root) return;
  root.querySelectorAll('[data-stg-colour]').forEach(function (button) {
    button.setAttribute('aria-pressed', button === colour ? 'true' : 'false');
  });
  root.querySelectorAll('[data-stg-sizes]').forEach(function (group) {
    group.hidden = group.dataset.stgSizes !== colour.dataset.stgColour;
    group.classList.toggle('is-ready', !group.hidden);
  });
});

/* "Change" on an added cap: reveal the picker again */
document.addEventListener('click', function (event) {
  const change = event.target.closest ? event.target.closest('[data-stg-change]') : null;
  if (!change) return;
  event.preventDefault();
  const bar = change.closest('[data-stg-rewards]');
  const picker = bar && bar.querySelector('[data-stg-claim-root]');
  if (!picker) return;
  picker.hidden = !picker.hidden;
  change.setAttribute('aria-expanded', picker.hidden ? 'false' : 'true');
  picker.classList.toggle('is-ready', !picker.hidden);
  if (!picker.hidden) {
    picker.querySelectorAll('[data-stg-sizes]:not([hidden])').forEach(function (g) { g.classList.add('is-ready'); });
    picker.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
});

document.addEventListener('click', function (event) {
  const button = event.target.closest ? event.target.closest('[data-stg-claim]') : null;
  if (!button) return;
  event.preventDefault();
  claim(button);
});

function scheduleCheck(event) {
  if (event && event.detail && event.detail.data && event.detail.data.source === 'stg-rewards') return;
  window.setTimeout(function () {
    enforce(0, true);
  }, 60);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleCheck);
document.addEventListener(ThemeEvents.discountUpdate, scheduleCheck);

if (document.querySelector('[data-stg-rewards][data-has-reward]') || getChoice()) enforce(0, false);
